// ============================================================
// components/stashcard/StashersStrip.tsx
//
// Horizontal, scroll-snapped strip of Stasher avatars + names.
// Friend chips appear first; the chip whose author owns the
// featured pull-quote is visually flagged.
//
// Data source: `useStashersStrip(friendIds)` from the detail store.
// ============================================================

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { StasherChip } from '../../store/stash-card-detail/types';

interface StashersStripProps {
  count: number;
  chips: StasherChip[];
  /** Compact size (used in right-rail layouts). */
  compact?: boolean;
  className?: string;
}

export function StashersStrip({
  count,
  chips,
  compact,
  className,
}: StashersStripProps): React.JSX.Element {
  const size = compact ? 'h-9 w-9 text-xs' : 'h-12 w-12 text-sm';
  const labelMaxW = compact ? 'max-w-[60px]' : 'max-w-[80px]';

  return (
    <div className={cn('w-full', className)}>
      {!compact && (
        <h3 className="mb-3 text-base font-bold tracking-tight">
          {count} Stashers
        </h3>
      )}
      <div
        className="-mx-1 flex gap-4 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ scrollSnapType: 'x proximity' }}
        role="list"
      >
        {chips.map(chip => (
          <div
            key={chip.id}
            role="listitem"
            className="flex shrink-0 flex-col items-center gap-1.5"
            style={{ scrollSnapAlign: 'start' }}
          >
            <Avatar
              className={cn(
                size,
                'border-2 border-card shadow-sm ring-0',
                chip.hasFeaturedNote && 'ring-2 ring-[var(--stash-blue)] ring-offset-2 ring-offset-card',
              )}
            >
              {chip.avatarUrl ? (
                <AvatarImage src={chip.avatarUrl} alt={chip.name} />
              ) : null}
              <AvatarFallback
                className={cn(
                  'font-bold',
                  chip.isFriend
                    ? 'bg-[var(--stash-blue)]/10 text-[var(--stash-blue)]'
                    : 'bg-secondary text-secondary-foreground',
                )}
              >
                {chip.initials}
              </AvatarFallback>
            </Avatar>
            <span
              className={cn(
                'truncate text-xs font-medium text-foreground',
                labelMaxW,
              )}
              title={chip.name}
            >
              {chip.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
